// src/authStore.ts
import { create } from 'zustand';
import { SignIn } from '../api/api';
import { usePermissionStore } from './usePermissionStore';

interface User {
  _id: number;
  name: string;
  email: string;
  role?: string;
}

interface AuthResponse {
  data: {
    profile: User;
    token: string;
  };
}

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  signIn: (email: string, password: string) => Promise<boolean>;
  signOut: () => void;
  setAuthData: (user: User, token: string) => void;
}

const storedToken = localStorage.getItem('token');
const storedUser = localStorage.getItem('user');

export const useAuthStore = create<AuthState>((set) => ({
  user: storedUser ? JSON.parse(storedUser) : null,
  token: storedToken,
  isAuthenticated: !!storedToken,
  signIn: async (email: string, password: string) => {
    console.log('[useAuthStore] signIn called with email:', email);
    
    try {
      const { data, error } = await SignIn<AuthResponse>(email, password);
      console.log('[useAuthStore] SignIn API response - data:', data, 'error:', error);

      if (error) {
        console.error('[useAuthStore] SignIn API error:', error);
        return false;
      }
      
      console.log('[useAuthStore] SignIn successful, setting auth state');
      // Store the auth data in the state if successful
      set({
        user: data.data.profile,
        token: data.data.token,
        isAuthenticated: true,
      });

      // store the token in the local storage
      console.log('[useAuthStore] Storing token and user in localStorage');
      localStorage.setItem('token', data.data.token);
      localStorage.setItem('user', JSON.stringify(data.data.profile));

      // Fetch permissions after login (only if not already fetched)
      const permissionState = usePermissionStore.getState();
      console.log('[useAuthStore] Permission state - hasFetched:', permissionState.hasFetched, 'isLoading:', permissionState.isLoading);
      
      if (!permissionState.hasFetched && !permissionState.isLoading) {
        console.log('[useAuthStore] Fetching permissions');
        usePermissionStore.getState().fetchPermissions();
      } else {
        console.log('[useAuthStore] Skipping permission fetch - already fetched or loading');
      }

      console.log('[useAuthStore] signIn returning true');
      return true;
    } catch (error) {
      console.error('[useAuthStore] Exception during signIn:', error);
      return false;
    }
  },
  signOut: () => {
    set({ user: null, token: null, isAuthenticated: false });
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    // Reset permissions on logout
    usePermissionStore.getState().reset();
  },
  setAuthData: (user: User, token: string) => {
    set({ user, token, isAuthenticated: true });
    // Fetch permissions when auth data is set (only if not already fetched)
    const permissionState = usePermissionStore.getState();
    if (!permissionState.hasFetched && !permissionState.isLoading) {
      usePermissionStore.getState().fetchPermissions();
    }
  },
}));
